import React, { useRef } from 'react';
import { Box, Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';

// Every row's count in an import response besides `frameworkId` (e.g. `rowsProcessed`,
// `nodesCreated`, `nodesReused`, `ageGroupsCreated` for nodes; `rowsProcessed`,
// `suggestionsCreated` for suggestions) — built generically so this component doesn't
// need to know which importer produced the result.
const summarizeImportResult = ({ frameworkId: _frameworkId, ...counts }) =>
  Object.entries(counts)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');

/**
 * "Download template" + "Import" pair for a curriculum v2 bulk-import endpoint —
 * identical on the Framework Nodes and Action Suggestions screens, only the thunks and
 * filename differ. A row's structural error comes back as `{message, errors: string[]}`
 * (whole-file atomic: nothing is written unless every row validates), so both are
 * surfaced together rather than just `message`.
 */
function CurriculumBulkImport({
  frameworkId,
  label,
  templateFilename,
  getTemplate,
  importAction,
  onImported,
}) {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();
  const fileInputRef = useRef(null);

  const showImportError = (error) => {
    console.error('rejected', error);
    ErrorToast(
      Array.isArray(error.errors) && error.errors.length
        ? `${error.message}: ${error.errors.join('; ')}`
        : error.message
    );
  };

  const handleDownloadTemplate = () => {
    handleLoader(true);
    dispatch(getTemplate({ frameworkId }))
      .unwrap()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', templateFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(showImportError)
      .finally(() => handleLoader(false));
  };

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    // Cleared so picking the exact same file again still fires onChange.
    e.target.value = null;
    if (!file) return;

    handleLoader(true);
    dispatch(importAction({ frameworkId, file }))
      .unwrap()
      .then((result) => {
        SuccessToast(summarizeImportResult(result));
        onImported();
      })
      .catch(showImportError)
      .finally(() => handleLoader(false));
  };

  return (
    <Box display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
      <Button
        onClick={handleDownloadTemplate}
        variant="outlined"
        color="primary"
        disabled={!frameworkId}>
        Download {label} Template
      </Button>
      <Button
        onClick={() => fileInputRef.current.click()}
        variant="outlined"
        color="primary"
        disabled={!frameworkId}>
        Import {label}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Box>
  );
}

export default CurriculumBulkImport;
